import { useTranslation } from 'next-i18next';
import SimpleLink from '_components/UI/SimpleLink';
import FindMySize from '../FindMySize';
import {
  FitSizeValueInterface,
  SizeChartInterface,
  SizeFitRiseInterface,
} from '@adapters/bigcommerce/types/sizeChart';

import styles from './SizeAndFit.module.scss';

export interface SizeAndFitProps {
  fitSentimentData?: {
    fitSentiment?: {
      percentageResults?: {
        runsLarge: number;
        runsSmall: number;
        trueToSize: number;
      };
      total: number;
    };
  };
  measure: SizeChartInterface;
  sizeDetail: SizeFitRiseInterface;
  fitAndRise: FitSizeValueInterface[] | null;
  isBike: boolean;
  scrollToProductReviewsAndAnswers?: () => void;
}

const FIT_BAR_SEGMENT_WIDTH_PX = 98;

const safeNumber = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const SizeAndFit = ({
  fitSentimentData,
  measure,
  sizeDetail,
  fitAndRise,
  isBike,
  scrollToProductReviewsAndAnswers,
}: SizeAndFitProps) => {
  const { t } = useTranslation('pdp');

  const percentageResults = fitSentimentData?.fitSentiment?.percentageResults;
  const total = safeNumber(fitSentimentData?.fitSentiment?.total);
  const hasFitSentiment = Boolean(percentageResults && total > 0);

  const runsSmall = clamp(safeNumber(percentageResults?.runsSmall), 0, 100);
  const trueToSize = clamp(
    safeNumber(percentageResults?.trueToSize),
    0,
    100 - runsSmall
  );

  const trueToSizeMidpoint = clamp(runsSmall + trueToSize / 2, 0, 100);
  const fitBarSegmentHalfWidth = FIT_BAR_SEGMENT_WIDTH_PX / 2;
  const fitBarSegmentRight = `clamp(0px, calc(${trueToSizeMidpoint}% - ${fitBarSegmentHalfWidth}px), calc(100% - ${FIT_BAR_SEGMENT_WIDTH_PX}px))`;

  return (
    <section className={styles.root}>
      {hasFitSentiment && (
        <div className={styles.fitSection} data-testid="size-and-fit-section">
          <div className={styles.headingRow}>
            <h3 className={styles.title}>
              {t('product.sizeAndFit.whatCustomersSay')}
            </h3>
            <SimpleLink
              href="#reviews-and-answers"
              onClick={scrollToProductReviewsAndAnswers ?? undefined}
              variant="secondary"
              size="small"
              className={styles.reviewsLink}
            >
              {t('product.sizeAndFit.viewAllReviews', { count: total })}
            </SimpleLink>
          </div>

          <div className={styles.fitBarSection}>
            <div className={styles.fitBarTrack}>
              <div
                className={styles.fitBarSegment}
                data-testid="size-and-fit-bar-segment"
                role="img"
                aria-label={t('product.sizeAndFit.fitBarMarker')}
                style={{
                  right: fitBarSegmentRight,
                  width: `${FIT_BAR_SEGMENT_WIDTH_PX}px`,
                }}
              />
            </div>
            <div className={styles.fitBarLabels}>
              <span>{t('product.sizeAndFit.feelsTooSmall')}</span>
              <span>{t('product.sizeAndFit.feelsTooLarge')}</span>
            </div>
          </div>
        </div>
      )}

      <ul className={styles.guidanceList}>
        <li>{t('product.sizeAndFit.guidance.1')}</li>
        <li>{t('product.sizeAndFit.guidance.2')}</li>
        <li>{t('product.sizeAndFit.guidance.3')}</li>
        <li>{t('product.sizeAndFit.guidance.4')}</li>
      </ul>
      <div className={styles.measureContainer}>
        <FindMySize measure={measure} sizeDetail={sizeDetail} isBike={isBike} />
      </div>
    </section>
  );
};

export default SizeAndFit;
